/**
 * Shared file/folder right-click context menu — the ONE menu used by both the
 * workspace explorer (features/workspace/ui/WorkspaceExplorer) and the
 * share-view explorer (apps/web/.../share/ShareFileExplorer). Presentational
 * only: the caller builds the item list (so each surface offers the subset it
 * supports — e.g. the share-view drops AI Access / Who-has-access / Reveal in
 * Drive / Link Drive Folder), this component renders them identically.
 *
 * Styling is the drive menu's, verbatim, so both surfaces look the same.
 */
import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { ChevronRight } from 'lucide-react'
import { useTheme, getThemeClass } from './ThemeProvider'
import type { GlyphIcon } from './PDAppIcon'

/** Widened to `GlyphIcon` so a hand-drawn glyph (PDAppIcon) can be a menu
 *  item's icon, not just a lucide one. */
type IconType = GlyphIcon

export type FileMenuItem =
  | { kind: 'separator' }
  /** Non-interactive uppercase section label (e.g. "Share"). Spans all columns. */
  | { kind: 'header'; label: ReactNode }
  | { kind: 'item'; icon?: IconType; label: ReactNode; shortcut?: string; danger?: boolean; disabled?: boolean; onClick: () => void; testId?: string }
  /** Upload entries are <label htmlFor> tied to a hidden <input type=file>, not buttons. */
  | { kind: 'upload'; icon?: IconType; label: ReactNode; htmlFor: string; disabled?: boolean; onClick?: () => void }
  /** Hover-expanded submenu (e.g. AI Access → Full / Read-only / Hide). */
  | {
      kind: 'submenu'
      icon: IconType
      label: ReactNode
      open: boolean
      onOpen: () => void
      onClose: () => void
      options: Array<{ label: ReactNode; onClick: () => void }>
    }

// Glass menu items (see .ws-menu* in index.css). The old opaque item styles
// migrated here so every surface using this menu picks up the glass design.
const ITEM_CLS = 'ws-menu-item'
const DANGER_CLS = 'ws-menu-item ws-menu-item-danger'

export function FileContextMenu({
  x, y, items, menuRef, testId, className = '', exiting = false, origin,
}: {
  x: number
  y: number
  items: FileMenuItem[]
  menuRef?: RefObject<HTMLDivElement | null>
  testId?: string
  /**
   * Anchored-panel state from `useAnchoredPosition`. `exiting` plays the
   * collapse-toward-the-anchor animation; `origin` is where it collapses TO.
   * Both optional — a caller still positioning by hand simply omits them and
   * the menu behaves exactly as before.
   */
  exiting?: boolean
  origin?: { x: number; y: number } | null
  /** Extra classes on the container (e.g. a theme class when rendered in a portal/overlay). */
  className?: string
}) {
  const { getTheme } = useTheme()
  // Portaled to <body>, so it escapes the workspace floating-canvas cards —
  // each `.ws-card` is `position:relative; z-index:1`, a stacking context
  // that traps an in-tree `position:fixed` menu *inside* the card, letting
  // the elevated editor/hero card paint over it (and its rounded overflow
  // clips the right edge). <body> is also outside every ThemedArea, so a
  // caller that doesn't pass its own theme class gets the workspace theme
  // re-applied here — otherwise `themed-*` utilities wouldn't resolve.
  const themeClass = className || getThemeClass(getTheme('workspace'))

  // Aligned columns (icon · label · shortcut). Rule: if ANY row carries an
  // icon, EVERY row reserves the icon column — icon or a blank slot — so all
  // labels start at the same x. Rows with no icons anywhere skip the column
  // entirely. `iconCell` renders that fixed-width slot.
  const hasIcon = items.some(it => it.kind !== 'separator' && it.kind !== 'header' && !!it.icon)
  const iconCell = (Icon?: IconType) =>
    hasIcon
      ? <span className="w-3.5 shrink-0 flex items-center justify-center">{Icon ? <Icon size={14} /> : null}</span>
      : (Icon ? <Icon size={14} /> : null)

  // Collapse separators: drop leading, trailing, and any that immediately
  // follow another separator. Lets callers push a separator unconditionally
  // per group without producing a double divider when a group is empty.
  const rows: FileMenuItem[] = []
  for (const it of items) {
    if (it.kind === 'separator' && (rows.length === 0 || rows[rows.length - 1].kind === 'separator')) continue
    rows.push(it)
  }
  while (rows.length && rows[rows.length - 1].kind === 'separator') rows.pop()

  // Keep the menu inside the viewport: measure after mount and clamp top/left so
  // a menu opened near the bottom/right edge doesn't spill offscreen. Runs in a
  // layout effect (before paint) so there's no visible jump.
  const innerRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: y, left: x })
  useLayoutEffect(() => {
    const el = innerRef.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    setPos({
      top: Math.max(8, Math.min(y, window.innerHeight - height - 8)),
      left: Math.max(8, Math.min(x, window.innerWidth - width - 8)),
    })
  }, [x, y])
  const assignRef = (el: HTMLDivElement | null) => {
    innerRef.current = el
    if (menuRef) (menuRef as { current: HTMLDivElement | null }).current = el
  }

  return createPortal(
    <div
      ref={assignRef}
      data-testid={testId}
      // w-max (width: max-content) pins the menu to its content width. Without
      // it, a fixed-position element sizes shrink-to-fit against `viewport − x`,
      // so opening near a right edge forces it narrower than its rows.
      // ws-menu-cols makes it a subgrid: icon · label · shortcut columns shared
      // across every row, so the widest label sizes the label column and the
      // shortcut column always sits to its right (no label bleeds under it).
      // z-[70] (#1426): above the mobile header (`MobileHeader`, z-[60]) — at z-50 the header painted over the menu's top
      // rows — and below the modal dialogs (z-[100] and up), whose own menus sit higher still (z-[300]). A few dialogs sit
      // at z-50/z-[60]: a menu opened from one paints above it, as it must.
      className={`${themeClass} ws-menu ws-menu-cols fixed z-[70] w-max min-w-[180px] max-h-[calc(100vh-16px)] overflow-y-auto${exiting ? ' anchor-exit' : ''}`}
      style={{
        top: pos.top, left: pos.left,
        gridTemplateColumns: hasIcon ? 'auto 1fr auto' : '1fr auto',
        ...(origin ? { transformOrigin: `${origin.x}px ${origin.y}px` } : {}),
      }}
    >
      {rows.map((it, i) => {
        if (it.kind === 'separator') return <div key={i} className="ws-menu-sep" />
        if (it.kind === 'header') return (
          <div key={i} style={{ gridColumn: '1 / -1' }} className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wider themed-text-muted select-none">
            {it.label}
          </div>
        )
        if (it.kind === 'upload') {
          // Disabled (e.g. a view-only share): render an inert, greyed button — NOT a
          // <label> — so it can't trigger the hidden file input.
          if (it.disabled) {
            return (
              <button key={i} disabled className={`${ITEM_CLS} opacity-40 cursor-not-allowed`}>
                {iconCell(it.icon)}
                {it.label}
              </button>
            )
          }
          // The consumer's onClick is DEFERRED, and that is load-bearing.
          //
          // A label opens the file picker through its native activation
          // behaviour, which the browser runs AFTER the click listeners have
          // returned. Every consumer closes the menu in that callback, and for
          // a discrete event React flushes the state update synchronously — so
          // the label was gone from the document before the browser got to
          // forward the click, and "Upload files…" silently did nothing while
          // the same upload reached by any other route worked fine.
          //
          // A macrotask puts the close after activation. Deferring is safe for
          // what these callbacks actually do (close the menu, stamp an upload
          // target ref) — the file `change` event is a user interaction away.
          return (
            <label
              key={i}
              htmlFor={it.htmlFor}
              onClick={() => { if (it.onClick) setTimeout(it.onClick, 0) }}
              className={`${ITEM_CLS} cursor-pointer`}
            >
              {iconCell(it.icon)}
              {it.label}
            </label>
          )
        }
        if (it.kind === 'submenu') {
          return <SubmenuRow key={i} item={it} iconCell={iconCell} themeClass={themeClass} />
        }
        return (
          <button
            key={i}
            data-testid={it.testId}
            disabled={it.disabled}
            onClick={it.disabled ? undefined : it.onClick}
            className={`${it.danger ? DANGER_CLS : ITEM_CLS}${it.disabled ? ' opacity-40 cursor-not-allowed' : ''}`}
          >
            {iconCell(it.icon)}
            {/* label column (min-w-0 + truncate so a long label clips inside its
                own column instead of pushing the shortcut column) */}
            <span className="min-w-0 truncate">{it.label}</span>
            {/* shortcut column — its own subgrid track, right-aligned */}
            {it.shortcut && <span className="justify-self-end text-[11px] themed-text-muted">{it.shortcut}</span>}
          </button>
        )
      })}
    </div>,
    document.body,
  )
}

type IconCell = (Icon?: IconType) => ReactNode

/**
 * A hover-expanded submenu row. The flyout is PORTALED to <body> rather than
 * rendered as a DOM child of the row, because the parent menu now has
 * `overflow-y-auto` (a tall-menu scroll clamp): per the CSS spec, a non-visible
 * `overflow-y` forces `overflow-x` to compute to `auto` too, so an in-tree
 * `absolute left-full` flyout gets clipped by the scroll container instead of
 * popping out to the side (it appears trapped inside the menu). Portaling to
 * <body> escapes the clip, exactly as the main menu itself does.
 */
function SubmenuRow({
  item, iconCell, themeClass,
}: {
  item: Extract<FileMenuItem, { kind: 'submenu' }>
  iconCell: IconCell
  themeClass: string
}) {
  const rowRef = useRef<HTMLButtonElement | null>(null)
  const flyoutRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  // Position the flyout against the trigger's right edge once it's open and
  // measured. Flip to the left side if it would spill past the right viewport
  // edge, and clamp the top so it doesn't run off the bottom. Runs before paint
  // so there's no visible jump. The flyout sits FLUSH with the row's right edge
  // (no gap) so the pointer can cross onto it without passing through dead space
  // that would fire onMouseLeave and close the submenu.
  useLayoutEffect(() => {
    if (!item.open) { setPos(null); return }
    const row = rowRef.current
    const fly = flyoutRef.current
    if (!row || !fly) return
    const r = row.getBoundingClientRect()
    const { width, height } = fly.getBoundingClientRect()
    let left = r.right
    if (left + width > window.innerWidth - 8) left = Math.max(8, r.left - width)
    const top = Math.max(8, Math.min(r.top, window.innerHeight - height - 8))
    setPos({ top, left })
  }, [item.open])

  return (
    <div className="relative" onMouseEnter={item.onOpen} onMouseLeave={item.onClose}>
      <button ref={rowRef} className={ITEM_CLS}>
        {iconCell(item.icon)}
        {item.label}
        <ChevronRight size={12} className="ml-auto" />
      </button>
      {item.open && createPortal(
        <div
          ref={flyoutRef}
          // Portaled to <body>, so it's OUTSIDE the caller's contextMenuRef. This
          // marker lets a caller's outside-click / selection-clear mousedown
          // guards recognize a click on a submenu option as still "inside the
          // menu" — without it, a capture-phase mousedown handler would dismiss
          // the menu before the option's onClick fires.
          data-file-context-submenu=""
          className={`${themeClass} ws-menu fixed min-w-[160px] z-[70]`}   // as its menu (#1426)
          style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
          onMouseEnter={item.onOpen}
          onMouseLeave={item.onClose}
        >
          {item.options.map((o, j) => (
            <button key={j} onClick={o.onClick} className="ws-menu-item">
              {o.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}
